import React, { useCallback, useState, useEffect, } from "react";
import Stack from "@mui/material/Stack";
import Slider from "@mui/material/Slider";
import Input from "@mui/material/Input";
import OptionSwitchInput from "../../Inputs/OptionSwitchInput";
import { t } from "i18next";
import TeachAndRepeatZonePath from "../../../interfaces/robot/rooms/teach-and-repeat-zone-path";
import { convertValueToCorrectMetric, getLimitedValue } from "../../../utils/util_functions";
import { TeachAndRepeatResolveType, getTeachAndRepeatResolveTypeList, } from "../../../enums/robot/rooms/teach-and-repeat-resolve-type";
import { useSharedDispatch, useSharedActions, } from "../../../store/SharedProvider";
import {
  allowedDeviationSliderMax,
  allowedDeviationSliderMin,
  allowedDeviationSliderStep,
} from "../../Configuration/CleaningConfiguration/NavigatorSettingsConfig";
import { DistanceUnit } from "../../../enums/configuration/units";

const switchOptionList = getTeachAndRepeatResolveTypeList.map((rt) => ({
  value: rt,
  label: t(rt),
  helpText: t(`${rt}-tooltip`)
}));

const useUpdateTeachAndRepeatParameters = () => {
    const dispatch = useSharedDispatch();
    const {
        patchCleaningZone
    } = useSharedActions();

    const updateTeachAndRepeatParameters = useCallback(
        ({trZone}:({trZone:Partial<TeachAndRepeatZonePath>})) => {
            if(trZone.zoneId != undefined){
                dispatch(patchCleaningZone({id: trZone.zoneId, data: { teachAndRepeatPath:trZone}}));
            }
        },[dispatch]
    );
    return updateTeachAndRepeatParameters;
}

const AllowedDeviationSlider: React.FC<{
  zoneId: string;
  value: number;
  distanceUnit: DistanceUnit;
  multiplier: number;
 }> = ({
  zoneId,
  value,
  distanceUnit,
  multiplier,
}) => {

  const convertToCorrectMetric = (value: number | null | undefined) => convertValueToCorrectMetric(value, distanceUnit, multiplier);
  const convertValueToMeter = (value: number) => {
      if (distanceUnit === DistanceUnit.METRIC) {
          return value;
      }
      return value / multiplier;
  };
  const [ stateValue, setStateValue ] = useState(convertToCorrectMetric(value));
  const allowedDeviationChangeHandler = useUpdateTeachAndRepeatParameters();
  const sliderStateChangeHandler = (newValue: number | undefined) => {
      if (newValue != null) {
          setStateValue(newValue);
      }
  };

  useEffect(() => {
      setStateValue(convertToCorrectMetric(value) ?? null);
  }, [value]);
  
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Slider
        value={stateValue!}
        onChange={(_: Event, value: number | number[], __: number) => sliderStateChangeHandler(value as number)}
        min={convertToCorrectMetric(allowedDeviationSliderMin)!}
        max={convertToCorrectMetric(allowedDeviationSliderMax)!}
        step={convertToCorrectMetric(allowedDeviationSliderStep)!}
        valueLabelDisplay="off"
        onChangeCommitted={(event, number) => {
          allowedDeviationChangeHandler({
            trZone: {
            zoneId,
            allowedDeviation: convertValueToMeter(number as number),
        }})
        }
      }
      />
      <Input
        value={value}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          allowedDeviationChangeHandler({
            trZone: {
            allowedDeviation: getLimitedValue(
              convertValueToMeter(+event.target.value),
              allowedDeviationSliderMin,
              allowedDeviationSliderMax
            ),
            zoneId,
          }})
        }
        type="number"
        sx={(theme) => ({ width: theme.spacing(12) })}
      />
    </Stack>
  );
};

const ErrorResolveSwitchInput: React.FC<{
  zoneId: string;
  value: TeachAndRepeatResolveType;
}> = ({ zoneId, value }) => {
  const resolveTypeChangeHandler = useUpdateTeachAndRepeatParameters();
  return (
    <OptionSwitchInput
      value={value}
      options={switchOptionList}
      onChange={(resolveConfig) =>
        resolveTypeChangeHandler({
          trZone: {
          resolveConfiguration: resolveConfig,
          zoneId,
        }})
      }
    />
  );
};

export {
  useUpdateTeachAndRepeatParameters,
  AllowedDeviationSlider,
  ErrorResolveSwitchInput,
};
